import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { PenTool, Upload, Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

function uploadHref(selectedFolder) {
  if (selectedFolder !== 'all' && selectedFolder !== 'root') {
    return `/editor/modules/upload?folder_id=${selectedFolder}`;
  }
  return '/editor/modules/upload';
}

export function EditorPageHeader({ totalCount, selectedFolder }) {
  const { hasRole } = useAuth();
  // Trainers see the Content surface read-only — hide the Upload + Blank
  // module call-to-actions. Backend `require_editor` would 403 anyway, but
  // hiding the buttons is cleaner than letting trainers click into a dead-end.
  const canEdit = hasRole('editor');
  const href = uploadHref(selectedFolder);
  return (
    <div className="flex items-end justify-between flex-wrap gap-4 pb-1">
      <div className="space-y-1.5">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] text-[11px] font-semibold uppercase tracking-[0.14em]">
          <PenTool className="w-3.5 h-3.5" />{'Content'}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-baseline gap-2">
          {canEdit ? 'Content editor' : 'Content library'}
          <span className="text-base font-medium text-muted-foreground">{totalCount} {totalCount === 1 ? 'module' : 'modules'}</span>
        </h1>
        <p className="text-sm text-muted-foreground max-w-prose">
          {canEdit
            ? 'Upload presentations, organize them into folders, and turn each section into a live quiz.'
            : 'Browse published modules — open any one to preview slides or launch a live session.'}
        </p>
      </div>
      {canEdit && (
        <div className="flex gap-2.5">
          <Button variant="outline" asChild data-testid="create-blank-btn">
            <Link to={href}><Plus className="w-4 h-4 mr-2" />{'Blank module'}</Link>
          </Button>
          <Button asChild data-testid="upload-btn" className="shadow-sm">
            <Link to={href}><Upload className="w-4 h-4 mr-2" />{'Upload presentation'}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
];

export function EditorStatusTabs({ tab, onChange, modules }) {
  return (
    <div className="flex gap-1 border-b">
      {TABS.map((tt) => {
        const count = tt.key === 'all' ? modules.length : modules.filter((m) => m.status === tt.key).length;
        const active = tab === tt.key;
        return (
          <button
            key={tt.key}
            onClick={() => onChange(tt.key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tt.key}`}
          >
            <span className="inline-flex items-center gap-1.5">
              {tt.label}
              <span className={`text-[11px] tabular-nums px-1.5 py-0.5 rounded-full ${
                active ? 'bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)]' : 'bg-muted text-muted-foreground'
              }`}>{count}</span>
            </span>
            {active && (
              <span aria-hidden className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--lumyn-content)] rounded-full" />
            )}
          </button>
        );
      })}
    </div>
  );
}
